// ============================================================================
// workspaceStore.ts — ЕДИНЫЙ источник текущего workspace (SSoT, SRP).
// Workspace = Page с kind='workspace'. Хранит активный workspace + список
// доступных. Переключение — через setWorkspace(code).
// ============================================================================

import { create } from 'zustand';

export interface Workspace {
  id: string;
  code: string;
  name: string;
  synonym: string;
  appKind: string;   // 'default' | 'messenger' | ...
  icon: string;      // лукап иконки
  layout: string;    // JSON макет
  /** Приложения в составе воркспейса (коды kind='app' — состав workspace; SSoT — сервер) */
  apps: string[];
}

interface WorkspaceState {
  /** Доступные workspace текущего пользователя */
  workspaces: Workspace[];
  /** Активный workspace (code) */
  activeCode: string | null;
  /** Основной workspace (код; SSoT — сервер ListMyWorkspaces.default_code) */
  defaultCode: string | null;
  /** Загружено (успешно) */
  loaded: boolean;
  setWorkspaces: (ws: Workspace[]) => void;
  setDefaultCode: (code: string | null) => void;
  setActiveCode: (code: string | null) => void;
  getActive: () => Workspace | null;
  /** Сбросить (смена базы — состав воркспейсов у каждой базы СВОЙ) */
  reset: () => void;
  /** Загрузить доступные воркспейсы с сервера (SSoT — ListMyWorkspaces).
   *  ЕДИНАЯ точка наполнения стора (WorkspaceShell/док/меню Пуск). */
  load: () => Promise<void>;
}

export const useWorkspaceStore = create<WorkspaceState>((set, get) => ({
  workspaces: [],
  activeCode: null,
  defaultCode: null,
  loaded: false,
  setWorkspaces: (ws) => set({ workspaces: ws, loaded: true }),
  setDefaultCode: (code) => set({ defaultCode: code }),
  setActiveCode: (code) => set({ activeCode: code }),
  getActive: () => {
    const { workspaces, activeCode } = get();
    if (!activeCode) return workspaces[0] || null;
    return workspaces.find((w) => w.code === activeCode) || workspaces[0] || null;
  },
  reset: () => set({ workspaces: [], activeCode: null, defaultCode: null, loaded: false }),
  load: async () => {
    const { getSDK } = await import('@/store/sdkStore');
    try {
      const r: any = await getSDK().page.listMyWorkspaces({});
      const pages = ((r as any)?.items || []).map((p: any) => ({
        id: p.id,
        code: p.code,
        name: p.name,
        synonym: p.synonym,
        appKind: p.appKind || p.app_kind || 'default',
        icon: p.icon || '',
        layout: p.layout || '',
        apps: Array.isArray(p.apps) ? p.apps.map(String) : [],
      }));
      const preferred = (r?.defaultCode as string)
        || pages.find((w: Workspace) => w.appKind === 'default')?.code
        || pages[0]?.code
        || null;
      set({
        workspaces: pages,
        loaded: true,
        defaultCode: (r?.defaultCode as string) || null,
        // Активный: прежний (если ещё доступен) ИЛИ основной «Предприятие»/первый
        activeCode: get().activeCode && pages.some((w: Workspace) => w.code === get().activeCode)
          ? get().activeCode
          : preferred,
      });
    } catch {
      // Ошибка загрузки (нет сессии/сети/старый сервер): НЕ помечаем loaded —
      // иначе повторная попытка (переоткрытие базы) никогда не выполнится.
      set({ loaded: false });
    }
  },
}));
